/**
 * How this package reaches the backend — supplied once by whichever app is running.
 *
 * WHY THIS IS ITS OWN PACKAGE, AND NOT PART OF `@ui`
 * -------------------------------------------------
 * `@ui` is full of `.svelte` files. The extension's background worker calls the same
 * routes as its panel and must never pull a component into a service worker, so the
 * client half lives where a worker can import it without dragging Svelte behind it.
 * The two concerns were only ever together because there was nowhere else to put them.
 *
 * WHY THE VERSION IS A DIRECTORY AND NOT A SUFFIX
 * ----------------------------------------------
 * The backend versions its routes and always will; `/v1/communities/{id}/posts/{id}`
 * is the real path and this package spells it out. What it does NOT do is repeat that
 * version in every identifier — `fetchPostThread`, not `fetchPostThread`. A name
 * says what the call DOES; the folder it sits in says which surface it speaks to. When
 * `/v2` arrives it gets a sibling directory, and the callers that move are the ones
 * whose shapes actually changed.
 *
 * TRANSPORT ONLY. What goes on the request — an admin session in the dashboard, a
 * 90-day device token in the Helper — is the app's business. What route to call, and
 * what the answer means, is this package's.
 */

export interface ApiTransport {
  /**
   * One authenticated GET. `path` is the REAL backend path, exactly as the API docs
   * spell it; there is no prefix to add and none implied.
   *
   * Contracted to REJECT on a non-2xx, like an ordinary client.
   */
  get<T>(path: string, params?: Record<string, string | number | string[]>): Promise<T>;

  /** One authenticated write. One method rather than four, because the apps differ in
   *  transport and in nothing else. Same contract as `get`. */
  send<T>(
    method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
    path: string,
    body?: unknown,
    params?: Record<string, string | number | string[]>,
  ): Promise<T>;

  /**
   * The same request, uninterpreted — auth attached, `Response` handed back as-is.
   *
   * For the one call no JSON client can express: the suggestion stream, which POSTs a
   * body and reads SSE frames. Contracted NOT to throw on a non-2xx; the caller reads
   * the status and the error envelope itself.
   */
  raw(path: string, init?: RequestInit): Promise<Response>;
}

/**
 * Sensible nothing.
 *
 * A call made before `configureApi` runs must fail loudly and locally rather than
 * silently returning undefined into a component that then renders a blank card.
 */
let transport: ApiTransport = {
  get: async () => {
    throw new Error('@api: configureApi() has not run — no transport installed');
  },
  send: async () => {
    throw new Error('@api: configureApi() has not run — no transport installed');
  },
  raw: async () => {
    throw new Error('@api: configureApi() has not run — no transport installed');
  },
};

/** Install the running app's transport. Call once, at startup, before anything fetches. */
export function configureApi(t: Partial<ApiTransport>): void {
  transport = { ...transport, ...t };
}

/** The active transport. Read through a function so nothing can capture the default at
 *  module-init and keep it forever. */
export function api(): ApiTransport {
  return transport;
}
